/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useState } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { mixins, colors } from '@blend/consumer-components';
import { BlIcon } from '@blend/shared-react';

import { CommonAPI } from '../../../../../api/common/responses';
import { UserGroup } from './UserGroup';
import { formatFullName } from '@utils/formatting';

export namespace UserList {
  export interface Props {
    usersStatus: UsersStatus;
    userGroupOnOneDevice?: CommonAPI.Session[];
    individualUsers?: CommonAPI.Participant[];
    admitBrowser(sessionId: string): Promise<void>;
    removeBrowser(sessionId: string): Promise<void>;
    startCall?(): Promise<void>;
    startVideoRecording?(): Promise<void>;
  }
}

export interface UsersStatus {
  statusKey: keyof CommonAPI.UserDataResponse;
  display: string;
}

const styles = StyleSheet.create({
  wrapper: {
    width: '100%',
  },
  headingWrapper: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    backgroundColor: colors.catalystLight01,
    ...mixins.padding(8),
    ...mixins.borderTop(1, 'solid', mixins.globalBorderColor),
  },
  dropdownIcon: {
    backgroundColor: 'transparent',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    transition: 'transform 0.4s ease-out',
    ...mixins.margin(0, 10, 0, 0),
    ...mixins.padding(0),
  },
  dropdownCollapsedIcon: {
    transform: 'rotate(-90deg)',
  },
  nameWrapper: {
    fontSize: 16,
    ...mixins.margin(8),
  },
});

// Show all browser sessions and users with the same status (In video call/Onboarding)
export const UserList: React.FC<UserList.Props> = ({
  usersStatus,
  userGroupOnOneDevice,
  individualUsers,
  admitBrowser,
  removeBrowser,
  startCall,
  startVideoRecording,
}) => {
  const [isListExpanded, setIsListExpanded] = useState(true);
  return (
    <div className={css(styles.wrapper)} data-it="UserList">
      <span className={css(styles.headingWrapper)}>
        {usersStatus.display} (
        {userGroupOnOneDevice ? userGroupOnOneDevice.length : individualUsers ? individualUsers.length : 0})
        <button
          data-it="ExpandButton"
          className={css(styles.dropdownIcon, !isListExpanded && styles.dropdownCollapsedIcon)}
          onClick={() => setIsListExpanded(!isListExpanded)}
        >
          <BlIcon icon="caret-2" size="xs" />
        </button>
      </span>
      {isListExpanded &&
        userGroupOnOneDevice &&
        userGroupOnOneDevice.map((userGroup, i) => {
          return (
            <UserGroup
              userGroup={userGroup}
              usersStatus={usersStatus.statusKey}
              key={userGroup.id}
              admitBrowser={admitBrowser}
              removeBrowser={removeBrowser}
              startCall={startCall}
              startVideoRecording={startVideoRecording}
              index={i + 1}
              data-it="UserGroup"
            />
          );
        })}
      {isListExpanded &&
        individualUsers &&
        individualUsers.map((user: CommonAPI.Participant) => {
          return (
            <div key={user.id} className={css(styles.nameWrapper)} data-it="it-ListOfUsers">
              {formatFullName(user)}
            </div>
          );
        })}
    </div>
  );
};
